
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Package {
  id: string;
  name: string;
  duration: string;
  price: number;
  features: string[];
  popular?: boolean;
  gradient: string;
}

export default function PackageSelection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

  const packages: Package[] = [
    {
      id: 'basic',
      name: t('basicPackage'),
      duration: t('oneHour'),
      price: 500,
      features: [
        t('basicFeature1'),
        t('basicFeature2'),
        t('basicFeature3'),
      ],
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      id: 'standard',
      name: t('standardPackage'),
      duration: t('sixHours'),
      price: 2000,
      features: [
        t('standardFeature1'),
        t('standardFeature2'),
        t('standardFeature3'),
        t('standardFeature4'),
      ],
      popular: true,
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      id: 'premium',
      name: t('premiumPackage'),
      duration: t('oneDay'),
      price: 3500,
      features: [
        t('premiumFeature1'),
        t('premiumFeature2'),
        t('premiumFeature3'),
        t('premiumFeature4'),
      ],
      gradient: 'from-orange-500 to-red-500',
    },
    {
      id: 'ultimate',
      name: t('ultimatePackage'),
      duration: t('oneWeek'),
      price: 20000,
      features: [
        t('ultimateFeature1'),
        t('ultimateFeature2'),
        t('ultimateFeature3'),
        t('ultimateFeature4'),
        t('ultimateFeature5'),
      ],
      gradient: 'from-green-500 to-teal-500',
    },
  ];

  const handleSelectPackage = (packageId: string) => {
    setSelectedPackage(packageId);
    // Navigate to captive portal with selected package
    navigate('/captive-portal', { state: { selectedPackage: packages.find(p => p.id === packageId) } });
  };

  return (
    <section id="packages" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
            {t('chooseYourPackage')}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('packageDescription')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {packages.map((pkg, index) => (
            <div
              key={pkg.id}
              className="relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                    {t('popular')}
                  </span>
                </div>
              )}

              <div className={`bg-gradient-to-r ${pkg.gradient} p-8 rounded-t-2xl text-white`}>
                <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">{pkg.price}</span>
                  <span className="text-lg opacity-90">{t('currency')}</span>
                </div>
                <p className="mt-2 opacity-90">{pkg.duration}</p>
              </div>

              <div className="p-8">
                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-1"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPackage(pkg.id)}
                  className={`w-full py-4 rounded-xl font-bold transition-all duration-300 whitespace-nowrap ${
                    selectedPackage === pkg.id
                      ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white'
                      : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 hover:from-blue-600 hover:to-green-600 hover:text-white'
                  }`}
                >
                  {selectedPackage === pkg.id ? t('selected') : t('selectPackage')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
